import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, RefreshCw, Save, Loader2, FileCheck2, AlertTriangle, Send, Printer, CheckCircle2, Trash2, Move, ScanLine } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import TraegerSendDialog from "@/components/TraegerSendDialog";
import TraegerProfileEditor, { MissingProfileHint } from "@/components/TraegerProfileEditor";
import TraegerPageSelector from "@/components/TraegerPageSelector";
import TraegerLivePreview from "@/components/TraegerLivePreview";
import TraegerPositionEditor from "@/components/TraegerPositionEditor";
import TraegerTemplates from "@/components/TraegerTemplates";
import TraegerReadiness from "@/components/TraegerReadiness";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

function UploadBox({ onFile, busy, label, cta = "PDF hochladen" }) {
  const ref = useRef(null);
  return (
    <div className="border-2 border-dashed border-slate-300 bg-white p-8 text-center" data-testid="traeger-upload-box">
      <FileCheck2 className="mx-auto text-slate-400 mb-3" size={28} strokeWidth={1.25} />
      <p className="text-sm text-slate-600 mb-4 max-w-md mx-auto">{label}</p>
      <input ref={ref} type="file" accept="application/pdf" className="hidden" data-testid="traeger-file-input"
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid="traeger-upload-btn"
        className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 text-sm hover:bg-slate-700 transition-colors disabled:opacity-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {cta}
      </button>
    </div>
  );
}

const AUTO_KEYS = new Set(["checkbox", "unterschrift_stempel", "slot_signature", "slot_stamp"]);

function FieldList({ fields: all, setFields, only }) {
  const fields = all.filter((f) => !/^slot_/.test(f.key || ""));
  const shown = only ? fields.filter((f) => only.has(f.fid)) : fields;
  const pages = [...new Set(shown.map((f) => f.page))];
  if (!fields.length) return <div className="text-sm text-amber-700">Keine ausfüllbaren Felder erkannt – bitte erneut analysieren.</div>;
  return pages.map((pg) => (
    <div key={pg} className="border border-slate-200 bg-white" data-testid={`traeger-page-${pg}`}>
      <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-overline text-slate-500 border-b">Seite {pg} im Vordruck</div>
      <div className="divide-y">
        {shown.filter((f) => f.page === pg).map((f) => (
          <div key={f.fid} className="grid sm:grid-cols-[220px_1fr] gap-2 px-4 py-2 items-center">
            <div className="text-xs text-slate-600">{f.label}{f.key === "checkbox" && <span className="text-slate-400"> (Kästchen: „X“ oder leer)</span>}{f.key === "unterschrift_stempel" && <span className="text-slate-400"> („X“ = Stempel/Unterschrift einsetzen, leer = nicht)</span>}{f.manual && <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-[10px] rounded" data-testid={`traeger-manual-badge-${f.fid}`}>manuell</span>}</div>
            <input value={f.value || ""} data-testid={`traeger-field-${f.fid}`} title={f.manual ? "Manuell geändert – wird nicht mehr automatisch aus der Akte aktualisiert" : "Automatisch aus der Akte"}
              onChange={(e) => setFields(fields.map((x) => (x.fid === f.fid ? { ...x, value: e.target.value, manual: true } : x)))}
              className={`w-full border px-2 py-1.5 text-sm ${f.manual ? "border-blue-500 bg-blue-50" : f.value ? "border-slate-200" : "border-amber-300 bg-amber-50/40"}`} placeholder="leer lassen oder ergänzen" />
          </div>
        ))}
      </div>
    </div>
  ));
}

function printPdf(blob) {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  frame.src = url;
  frame.onload = () => {
    try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch (e) { window.open(url, "_blank"); }
    setTimeout(() => frame.remove(), 60000);
  };
  document.body.appendChild(frame);
}

const EXPECTED_SEC = 100;

function AnalyzeProgress({ form }) {
  const start = new Date(form.analysis_started_at || form.created_at).getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const sec = Math.max(0, Math.round((now - start) / 1000));
  const pct = Math.min(97, Math.round((sec / EXPECTED_SEC) * 100));
  const left = EXPECTED_SEC - sec;
  return (
    <div className="border border-slate-200 bg-white p-6 space-y-3" data-testid="traeger-analyzing">
      <div className="flex items-center gap-3 text-sm text-slate-600">
        <Loader2 size={16} className="animate-spin" /> „{form.filename}“ wird analysiert – die KI sucht die Felder und füllt sie aus.
      </div>
      <div className="h-2 bg-slate-100 overflow-hidden" data-testid="traeger-progress-bar">
        <div className="h-full bg-[#00807E] transition-[width] duration-1000 ease-linear" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between text-xs text-slate-500" data-testid="traeger-progress-text">
        <span>{pct} %</span>
        <span>{left > 0 ? `noch ca. ${left >= 60 ? `${Math.ceil(left / 60)} Min.` : `${left} Sek.`}` : "dauert etwas länger als üblich – gleich fertig …"}</span>
      </div>
      <p className="text-[11px] text-slate-400">Du kannst die Seite verlassen – sobald alles fertig ist, bekommst du eine Meldung über die Glocke.</p>
    </div>
  );
}

export default function TraegerFormPanel({ kind, personId, onChanged }) {
  const docName = kind === "client" ? "Trägerbestätigung" : "Bildungsgutschein";
  const [form, setForm] = useState(undefined);
  const [fields, setFieldsState] = useState([]);
  const [missing, setMissing] = useState(null);
  const setFields = (list, fresh) => {
    setFieldsState(list);
    if (fresh) setMissing(new Set(list.filter((f) => !AUTO_KEYS.has(f.key) && !/^modul_\d_/.test(f.key || "") && !/ggf\.|optional/i.test(f.label || "") && !(f.value || "").trim()).map((f) => f.fid)));
  };
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [posOpen, setPosOpen] = useState(false);
  const [prof, setProf] = useState(null);

  const load = useCallback(() => api.get(`/traeger-forms/${kind}/${personId}`).then((r) => { setForm(r.data); setFields(r.data?.fields || [], true); }).catch(err), [kind, personId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (form?.status !== "analyzing") return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [form?.status, load]);
  const prevStatus = useRef(null);
  useEffect(() => {
    if (prevStatus.current === "analyzing" && form?.status && form.status !== "analyzing") onChanged?.();
    prevStatus.current = form?.status;
  }, [form?.status]); // eslint-disable-line

  const upload = async (file) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/traeger-forms/${kind}/${personId}`, fd, { timeout: 120000 });
      setForm(r.data); setFields([], true);
      toast.success("Vordruck hochgeladen – KI erkennt jetzt die Felder (ca. 1 Minute)");
      onChanged?.();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const save = () => api.put(`/traeger-forms/form/${form.form_id}/fields`, { fields: fields.map(({ fid, value }) => ({ fid, value: value || "" })) });
  const download = async (full) => {
    setBusy(true);
    try {
      await save();
      const r = await api.get(`/traeger-forms/form/${form.form_id}/pdf`, { params: { full }, responseType: "blob", timeout: 180000 });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = full ? "Gutschein_komplett_ausgefuellt.pdf" : "Traegerbestaetigung_ausgefuellt.pdf";
      a.click();
      onChanged?.();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const print = async () => {
    setBusy(true);
    try {
      await save();
      const r = await api.get(`/traeger-forms/form/${form.form_id}/pdf`, { responseType: "blob", timeout: 180000 });
      printPdf(new Blob([r.data], { type: "application/pdf" }));
      onChanged?.();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Hochgeladenen Vordruck „${form.filename}" wirklich löschen? Alle Eingaben dazu gehen verloren.`)) return;
    try {
      await api.delete(`/traeger-forms/form/${form.form_id}`);
      setForm(null); setFields([], true);
      toast.success("Vordruck gelöscht");
      onChanged?.();
    } catch (e) { err(e); }
  };
  const readVoucher = async () => {
    setBusy(true);
    try {
      let { data } = await api.post(`/traeger-forms/form/${form.form_id}/read-voucher`, {}, { timeout: 90000 });
      if (data.pending) {
        toast.info(data.message || "Die KI liest den Gutschein – bitte einen Moment warten");
        for (let i = 0; i < 40 && data.pending; i += 1) {
          await new Promise((r) => setTimeout(r, 5000));
          const st = await api.get(`/traeger-forms/form/${form.form_id}/voucher-status`);
          if (st.data.ready) {
            ({ data } = await api.post(`/traeger-forms/form/${form.form_id}/read-voucher`, {}, { timeout: 90000 }));
          } else if (st.data.error && !st.data.running) {
            toast.error(`${st.data.error} – bitte noch einmal auf „Gutschein auslesen“ klicken`);
            return;
          }
        }
        if (data.pending) {
          toast.error("Die KI-Lesung dauert ungewöhnlich lange – bitte in einer Minute erneut auf „Gutschein auslesen“ klicken");
          return;
        }
      }
      setFields(data.fields, true);
      const f = data.found || {};
      const read = [f.massnahme_ziel && "Ziel", f.total_ue && `${f.total_ue} UE`, f.umfang_pro_woche, f.tage_pro_woche && `${f.tage_pro_woche} Tage/Woche`,
        f.gutschein_gueltig_von && `gültig ${f.gutschein_gueltig_von}–${f.gutschein_gueltig_bis || "?"}`, f.gutschein_nummer && `Nr. ${f.gutschein_nummer}`].filter(Boolean).join(" · ");
      const n = (data.filled || []).length;
      toast.success(`Gelesen: ${read || "—"}`, {
        description: n ? `${n} Feld(er) im Vordruck ausgefüllt: ${data.filled.join(", ")}` : "Im Vordruck gibt es dafür keine passende Zeile – bei Bedarf über „Bearbeiten & Positionen → Textfeld hinzufügen“ eintragen.",
        duration: 12000,
      });
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const act = (path, msg) => api.post(`/traeger-forms/form/${form.form_id}/${path}`, {}, { timeout: 240000 }).then((r) => { toast.success(msg); r.data.fields ? setFields(r.data.fields, true) : load(); }).catch(err);

  if (form === undefined) return <div className="text-xs text-slate-400 p-4">Lädt…</div>;
  const btn = "inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-sm hover:bg-slate-50 transition-colors disabled:opacity-50";

  return (
    <div className="space-y-4" data-testid="traeger-panel">
      <div>
        <h3 className="text-base md:text-lg font-medium text-slate-900">Trägerbestätigung</h3>
        <p className="text-xs text-slate-500 mt-1">Den Gutschein/Vordruck des Kunden (PDF vom Jobcenter bzw. der Agentur) hochladen – das Portal erkennt die Felder und füllt sie mit den Kundendaten aus. Vor dem Download alles kurz prüfen.</p>
      </div>
      <MissingProfileHint missing={prof?.missing} />
      <TraegerProfileEditor collapsible onSaved={load} onProfile={setProf} />
      {!form && <UploadBox onFile={upload} busy={busy} cta={`${docName} hochladen`} label={`Jeder Kunde bringt seine eigene ${docName} vom Jobcenter bzw. der Agentur mit – es gibt keine Vorlage. Lade sie hier hoch: Maßnahmezeitraum, UE, Beginn/Ende usw. trägt das System aus der Kundenakte ein.`} />}
      {form?.status === "analyzing" && <AnalyzeProgress form={form} />}
      {form?.status === "failed" && (
        <div className="border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 flex flex-wrap items-center gap-3" data-testid="traeger-failed">
          <AlertTriangle size={16} /> Analyse fehlgeschlagen{form.error ? `: ${form.error}` : "."} <button className={btn} onClick={() => act("reanalyze", "Analyse neu gestartet")} data-testid="traeger-reanalyze-btn"><RefreshCw size={14} /> Erneut versuchen</button>
        </div>
      )}
      {form?.status === "ready" && (
        <>
          <div className="border border-[#0a788c]/30 bg-[#0a788c]/5 px-4 py-2.5 text-xs text-slate-700" data-testid="traeger-check-hint">
            <b>Bitte zuerst die Live-Vorschau prüfen.</b> Stimmt alles, kannst du die {docName} herunterladen, drucken, speichern oder an den Kunden bzw. das Jobcenter senden.
          </div>
          <TraegerReadiness formId={form.form_id} beforeCheck={save} />
          <div className="flex flex-wrap gap-2">
            <button onClick={print} disabled={busy} className="inline-flex items-center gap-2 bg-[#0a788c] text-white px-5 py-2 text-sm hover:bg-[#086373] transition-colors disabled:opacity-50" data-testid="traeger-print-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />} Drucken
            </button>
            <button onClick={() => download(false)} disabled={busy} className={btn} data-testid="traeger-download-btn">
              <Download size={14} /> Herunterladen
            </button>
            <button onClick={() => setSendOpen(true)} disabled={busy} className={btn} data-testid="traeger-send-btn"><Send size={14} /> Senden (Kunde / Jobcenter)</button>
            <button onClick={() => save().then(() => toast.success("Gespeichert")).catch(err)} className={btn} data-testid="traeger-save-btn"><Save size={14} /> Speichern</button>
            <button onClick={() => act("refill", "Kundendaten neu übernommen")} className={btn} data-testid="traeger-refill-btn"><RefreshCw size={14} /> Kundendaten neu übernehmen</button>
            <button onClick={readVoucher} disabled={busy} className={btn} data-testid="traeger-read-voucher-btn"><ScanLine size={14} /> Gutschein auslesen</button>
            <button onClick={() => save().then(() => setPosOpen(true)).catch(err)} className={btn} data-testid="traeger-positions-btn"><Move size={14} /> Bearbeiten &amp; Positionen</button>
          </div>
          <TraegerPositionEditor open={posOpen} onClose={() => setPosOpen(false)} form={form} fields={fields} onSaved={(list) => setFields(list, false)} />
          <TraegerTemplates formId={form.form_id} onApplied={load} />
          <TraegerPageSelector form={form} onChange={(ex) => setForm((f) => ({ ...f, excluded_pages: ex }))} />
          <div className="text-xs text-slate-500">Vordruck: {form.filename} · ausgefüllte Seiten: {(form.form_pages || []).join(", ") || "—"} · Werte kommen live aus der Akte (UE, Beginn/Ende, Module) · gelb = leer · blau = manuell geändert („Kundendaten neu übernehmen“ setzt zurück)</div>
          <div className="grid lg:grid-cols-2 gap-4 items-start">
            <div className="space-y-4 min-w-0">
              {missing?.size > 0 ? (
                <div className="space-y-2" data-testid="traeger-missing-fields">
                  <div className="text-sm font-medium text-amber-800">Bitte ergänzen – diese Angaben hat das System nicht ({missing.size})</div>
                  <FieldList fields={fields} setFields={setFields} only={missing} />
                </div>
              ) : (
                <div className="border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="traeger-all-filled"><CheckCircle2 size={15} /> Alle Felder wurden automatisch ausgefüllt.</div>
              )}
              <details className="text-sm" data-testid="traeger-all-fields-toggle">
                <summary className="cursor-pointer text-slate-500">Alle Felder prüfen ({fields.length})</summary>
                <div className="space-y-4 mt-3"><FieldList fields={fields} setFields={setFields} /></div>
              </details>
            </div>
            <TraegerLivePreview formId={form.form_id} fields={fields} pagesKey={(form.excluded_pages || []).join(",")} />
          </div>
          <TraegerSendDialog open={sendOpen} onClose={() => { setSendOpen(false); onChanged?.(); }} formId={form.form_id} beforeOpen={save} />
        </>
      )}
      {form && form.status !== "analyzing" && (
        <button onClick={remove} className="inline-flex items-center gap-2 text-sm text-rose-700 hover:text-rose-900 transition-colors" data-testid="traeger-delete-btn">
          <Trash2 size={14} /> Hochgeladenen Vordruck löschen
        </button>
      )}
      {form && form.status !== "analyzing" && (
        <details className="text-sm"><summary className="cursor-pointer text-slate-500" data-testid="traeger-new-upload-toggle">Anderen Vordruck hochladen</summary>
          <div className="mt-3"><UploadBox onFile={upload} busy={busy} cta={`${docName} hochladen`} label="Ersetzt den aktuellen Vordruck dieses Kunden." /></div>
        </details>
      )}
    </div>
  );
}
